import { useRef, useReducer, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router';
import ReactGA from 'react-ga4';
import validate from '../../../validation/schemas/coach-signup';
import { Col, Row } from '../../../components/Grid';
import { Typography as T, Inputs as I, ReCaptcha } from '../../../components';
import { BasicInput } from '../../../components/Inputs';
import { BasicButton } from '../../../components/Button';
import Checkbox from '../../../components/Inputs/Checkbox/';
import Tip from '../../../components/Tip';
import { Coaches } from '../../../api-calls';
import { useAuth } from '../../../context/auth';
import Loading from '../../../components/Loading';
import { navRoutes, userRoles } from '../../../constants';
import { cleanEmail } from '../../../helpers';

const { COACH, EXTERNAL } = navRoutes;

function reducer(state, newState) {
  let value = newState;
  if (typeof newState === 'function') {
    value = newState(state);
  }
  return { ...state, ...value };
}

const tipString = `
Password must contain:
  * Minimum of 8 characters
  * One capital letter
  * One lowercase letter
  * One number
  * One special character
`;

const CoachSignup = () => {
  const { signupToken } = useParams();
  const initialState = {
    form: {
      coachId: '',
      token: signupToken,
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      giveConsent: false,
      shareData: false,
    },
    httpError: '',
    validationErrs: {},
    loading: false,
  };
  const [state, setState] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const submitAttempt = useRef(false);

  const {
    form: { firstName, lastName, email, password, giveConsent, shareData },
    loading,
    validationErrs,
    httpError,
  } = state;

  const setFormData = (data) => {
    setState((prevState) => ({
      httpError: '',
      form: { ...prevState.form, ...data },
    }));
  };

  const validateForm = () => {
    try {
      validate(state.form);
      setState({ validationErrs: { hasError: false } });
      return true;
    } catch (error) {
      if (error.name === 'ValidationError') {
        setState({
          validationErrs: { ...error.inner, hasError: true },
        });
      }
      return false;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitAttempt.current = true;

    const isValid = validateForm();
    if (isValid) {
      handleCoachSignup();
    }
  };

  const handleCoachSignup = async () => {
    setState({ loading: true });
    const { data, error } = await Coaches.signup({
      form: { ...state.form, email: cleanEmail(email) },
    });
    setState({ loading: false });
    if (error) {
      setState({
        httpError: error.message,
        validationErrs:
          error?.data?.field === 'email'
            ? {
                ...validationErrs,
                email: error.message,
              }
            : { ...validationErrs },
      });
    } else {
      setUser(data);
      if (data.role === userRoles.LEARNER || data.role === userRoles.COACH) {
        ReactGA.event({
          category: 'sign_up',
          action: 'sign_up',
        });
        ReactGA.set({
          ...data,
        });
      }
      // history.push(COACH.WELCOME); Refactor when adding welcome page
      navigate(COACH.DASHBOARD);
    }
  };

  const getCoach = async () => {
    setState({ loading: true });
    const { data, error } = await Coaches.getCoachBySignupToken({
      token: signupToken,
    });

    if (error) {
      navigate(navRoutes.COACH.DASHBOARD);
    } else {
      setState((prevState) => ({
        form: {
          ...prevState.form,
          coachId: data.coachId,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
        },
        loading: false,
      }));
    }
  };

  useEffect(() => {
    getCoach();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupToken]);

  useEffect(() => {
    if (submitAttempt.current) {
      validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName, email, password]);

  if (loading) return <Loading />;

  return (
    <>
      <Row>
        <Col w={[4, 12, 12]}>
          <T.H2 weight="bold" color="neutralMain">
            Create account
          </T.H2>
        </Col>
        <Col w={[4, 11, 6]} mt="5">
          <I.BasicInput
            label="Enter your first name"
            placeholder="Name..."
            type="text"
            value={firstName}
            autoFocus
            handleChange={(firstName) => setFormData({ firstName })}
            error={validationErrs.firstName}
          />
        </Col>
        <Col w={[4, 11, 6]} mt="5">
          <I.BasicInput
            label="Enter your last name"
            placeholder="Name..."
            type="text"
            value={lastName}
            handleChange={(lastName) => setFormData({ lastName })}
            error={validationErrs.lastName}
          />
        </Col>
        <Col w={[4, 11, 6]} mt="5">
          <BasicInput
            label="Enter your email "
            placeholder="Email..."
            value={email}
            name="email"
            handleChange={(email) => setFormData({ email })}
            error={validationErrs.email}
          />
        </Col>
        <Col w={[4, 11, 6]} mt="5">
          <BasicInput
            label="Create a password"
            placeholder="Password..."
            value={password}
            name="password"
            type="password"
            handleChange={(password) => setFormData({ password })}
            error={validationErrs.password}
          />
        </Col>

        <Col w={[4, 11, 6]} mt="3">
          <Tip coachNotesStyle={true} ml={0} text={tipString} p="12px 16px" />
          <Checkbox
            mt="4"
            plain
            font="regular"
            checked={giveConsent}
            handleChange={(giveConsent) => setFormData({ giveConsent })}
            label={
              <T.P color="neutralMain">
                By creating an account I give consent to Turning Pages using and
                keeping the information I provide as described in our{' '}
                <T.Link
                  to={EXTERNAL.PRIVACY_POLICY}
                  color="neutralMain"
                  external
                  underline
                >
                  Privacy Policy
                </T.Link>
              </T.P>
            }
          />
          <Checkbox
            plain
            font="regular"
            handleChange={(shareData) => setFormData({ shareData })}
            checked={shareData}
            label={
              <T.P color="neutralMain">
                Do you give consent for your details to be shared with your
                learner and programme manager?
              </T.P>
            }
            mb={4}
          />
          <BasicButton
            variant="primary"
            disabled={httpError || loading || !giveConsent || !shareData}
            loading={loading}
            type="submit"
            handleClick={handleSubmit}
          >
            <T.P color="white" weight="semi">
              Create account
            </T.P>
          </BasicButton>
        </Col>
      </Row>
      <Row>
        <Col w={[4, 11, 6]}>
          <ReCaptcha />
        </Col>
      </Row>
    </>
  );
};

export default CoachSignup;
